import { Component, Inject, OnInit } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
} from '@angular/material/dialog';
import { ContainerService } from '../../../services/container/container.service';
import {
    Container,
    ContainerJoinRequest,
    ContainerJoinRequestAnswer,
    ContainerJoinRequestList,
    ContainerMembership,
    ContainerType,
} from '../../../models/container.types';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { PagingResult } from '../../../models/pageable';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../../confirmation-modal/confirmation-dialog.component';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';

export class ContainerMembersDialogData {
    container: Container;
    joinRequests: ContainerJoinRequestList;
}

export const MAT_DIALOG_CONFIG_DATA: ConfirmationDialogData = {
    headerTranslationKey:
        'containerMembersAdministration.deleteMemberDialog.header',
    confirmationQuestion: {
        translationKey:
            'containerMembersAdministration.deleteMemberDialog.confirmationQuestion',
        interpolateParameters: null,
    },
    confirmButtonTranslationKey:
        'containerMembersAdministration.deleteMemberDialog.confirm',
    rejectButtonTranslationKey:
        'containerMembersAdministration.deleteMemberDialog.reject',
    transferData: true,
};

@Component({
    selector: 'app-container-members',
    templateUrl: './container-members.component.html',
    styleUrls: [
        './container-members.component.scss',
        '../container-administration-shared-styles.scss',
    ],
    standalone: false,
})
export class ContainerMembersComponent implements OnInit {
    container: Container;
    isCloseButtonHovered: boolean;

    members: ContainerMembership[];
    joinRequests: ContainerJoinRequestList;

    hoveredMemberListEltIdx: number = -1;
    hoveredRequestListAcceptBtnEltIdx: number = -1;
    hoveredRequestListRejectBtnEltIdx: number = -1;

    matDialogConfig: MatDialogConfig<ConfirmationDialogData>;
    selectedRequestIndices: Set<number> = new Set();
    grantOfUseChecked: boolean = false;

    constructor(
        private dialog: MatDialog,
        private containerService: ContainerService,
        public dialogRef: MatDialogRef<
            ContainerMembersComponent,
            ContainerMembersDialogData
        >,
        @Inject(MAT_DIALOG_DATA)
        public injectedData: ContainerMembersDialogData,
        private toastService: ToastMessageService
    ) {
        this.container = this.injectedData.container;
        this.joinRequests = this.injectedData.joinRequests;
        this.dialogRef.addPanelClass('container-administration-dialog');
    }

    ngOnInit() {
        this.getMembers().subscribe((result) => {
            this.members = result.values;
        });

        this.matDialogConfig = new MatDialogConfig();
        this.matDialogConfig.data = MAT_DIALOG_CONFIG_DATA;
    }

    getMembers(): Observable<PagingResult<ContainerMembership>> {
        return this.containerService
            .getContainerMembersWithoutOwner(this.container.id)
            .pipe(
                catchError(() => {
                    this.toastService.showTranslatedErrorMsg(
                        'containerMembersAdministration.errors.getMembers',
                        {}
                    );
                    return of(new PagingResult<ContainerMembership>());
                })
            );
    }

    closeModal() {
        this.dialogRef.close();
    }

    deleteMember(index: number) {
        const memberToDelete = this.members[index];

        this.dialog
            .open(ConfirmationDialogComponent, this.matDialogConfig)
            .afterClosed()
            .pipe(
                switchMap((res) => {
                    if (res) {
                        this.members.splice(index, 1);
                        return this.containerService.deleteMembership(
                            this.container.id,
                            memberToDelete.memberId
                        );
                    }
                    return of(null);
                })
            )
            .subscribe();
    }

    isRequestSelected(index: number): boolean {
        return this.selectedRequestIndices.has(index);
    }

    toggleRequestSelection(index: number): void {
        if (this.isRequestSelected(index)) {
            this.selectedRequestIndices.delete(index);
        } else {
            this.selectedRequestIndices.add(index);
        }
    }

    /**
     * Accept or decline all selected requests. Accept is only valid when grantOfUseChecked is true.
     * Processes selected indices in descending order so splicing does not invalidate indices.
     */
    answerSelectedRequests(accepted: boolean): void {
        if (accepted && !this.grantOfUseChecked) {
            return;
        }
        if (this.selectedRequestIndices.size === 0) {
            return;
        }

        const displayNames: string[] = [];
        this.selectedRequestIndices.forEach((selectedIndex: number) => {
            displayNames.push(
                this.joinRequests.values[selectedIndex].userAccountDisplayName
            );
        });
        const answer: ContainerJoinRequestAnswer = {
            containerId: this.container.id,
            displayNames,
            accepted,
            acceptedGrantingUsageRights: this.grantOfUseChecked,
        };

        this.containerService
            .answerJoinRequest(answer)
            .pipe(
                tap(() => {
                    this.selectedRequestIndices.clear();
                    this.joinRequests.values = this.joinRequests.values.filter(
                        (request: ContainerJoinRequest) =>
                            !displayNames.includes(
                                request.userAccountDisplayName
                            )
                    );
                }),
                catchError(() => {
                    this.toastService.showTranslatedErrorMsg(
                        'containerMembersAdministration.errors.answerJoinRequests',
                        {}
                    );
                    return of(null);
                }),
                switchMap(() => this.getMembers())
            )
            .subscribe({
                next: (members: PagingResult<ContainerMembership>) => {
                    this.members = members.values;
                },
            });
    }

    getTranslationKeyForContainerType(): 'project' | 'imgDb' {
        return this.container.containerType === ContainerType.IMAGE_DB
            ? 'imgDb'
            : 'project';
    }
}
